/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * "Download evidence pack" (GET /api/evidence/<deployments|transfers>/<id>.pdf): builds the signed
 * PDF on the portal's server with the user's session, saves it, and says which document it was
 * (its evidence ID and the SHA-256 of its canonical record) in a toast, so it can be quoted in a
 * change ticket.
 */

import * as React from "react";
import { FileDown, LoaderCircle } from "lucide-react";

import { ActionButton } from "@src/components/ui/DetailsKit";
import evidenceSource from "@src/sources/EvidenceSource";
import notificationStore from "@src/stores/NotificationStore";
import DomUtils from "@src/utils/DomUtils";

import type { EvidenceKind } from "@src/sources/EvidenceSource";

type Props = {
  kind: EvidenceKind;
  id: string;
  label?: string;
  disabled?: boolean;
  title?: string;
};

export const evidenceToast = (result: { fileName: string; evidenceId: string | null; sha256: string | null }) => {
  const parts = [
    result.evidenceId || null,
    result.sha256 ? `SHA-256 ${result.sha256.slice(0, 12)}…${result.sha256.slice(-6)}` : null,
  ].filter(Boolean);
  if (!parts.length) parts.push(result.fileName);
  notificationStore.alert(
    parts.join(" · "),
    "success",
    {
      action: result.sha256
        ? {
            label: "Copy SHA-256",
            callback: () => DomUtils.copyTextToClipboard(result.sha256!, "SHA-256 copied"),
          }
        : undefined,
    },
    "Evidence pack downloaded",
  );
};

const EvidenceButton = (props: Props) => {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    if (busy) return;
    setBusy(true);
    try {
      evidenceToast(await evidenceSource.download(props.kind, props.id));
    } catch (err: any) {
      notificationStore.alert(`Evidence pack: ${err?.message || err}`, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <ActionButton
      type="button"
      variant="secondary"
      onClick={download}
      disabled={busy || props.disabled}
      // Were "A signed PDF of …": the pack carries a SHA-256 verification hash and boxes for manual
      // signatures, not a digital signature.
      title={
        props.title ||
        (props.kind === "deployment"
          ? "A PDF of this migration, with a verification hash: what moved, when, who approved it, and every task's result"
          : "A PDF of this transfer, with a verification hash: its syncs, schedules and deployments")
      }
      data-test-id="evidenceButton"
    >
      {busy ? <LoaderCircle size={16} className="spin" /> : <FileDown size={16} />}
      {busy ? "Building the evidence pack…" : props.label || "Download evidence pack"}
    </ActionButton>
  );
};

export default EvidenceButton;
